/**
 * Notification Metadata Type Definitions
 *
 * This file contains TypeScript type definitions for notification metadata properties.
 * These types define the structure of the `notification.metadata` object for each
 * notification type, enabling type-safe access to notification data.
 *
 * Source: Extracted from backend types.ts (EventPayloadId property types)
 */

// ================================
// NOTIFICATION TYPE ENUM
// ================================

/**
 * EventPayloadId enum defines all possible notification types in the system
 * This is the authoritative source of truth for notification type strings
 */
export enum EventPayloadId {
  CASE_AVAILABLE_PROSPECT = 'case__available__prospect',
  CASE_EXPIRED_OWNER = 'case__expired__owner',
  CASE_EXPIRED_PROPOSAL_CASE_OWNER = 'case__expired_proposal__case_owner',
  CASE_EXPIRED_PROPOSAL_PROSPECT = 'case__expired_proposal__prospect',
  CASE_PROPOSAL_REMINDER_CASE_OWNER = 'case__proposal_reminder__case_owner',
  CASE_WATERFALL_INVITATION_EXPIRED_PROSPECT = 'case_waterfall_invitation_expired_prospect',
  CASE_PRIVATE_EXPIRING_WATERFALL_PROSPECT = 'Case_Private_Expiring_Waterfall_Prospect',
  CASE_FEE_AGREEMENT_REMINDER = 'case__fee_agreement_reminder',
  CASE_FEE_AGREEMENT_REMINDER_BUYER = 'case__fee_agreement_reminder__buyer',
  CASE_FEE_AGREEMENT_UPLOADED = 'case__fee_agreement_uploaded',
  CASE_STATUS_UPDATE_REMINDER_BUYER = 'case__status_update_reminder__buyer',
  CASE_STATUS_UPDATE_REQUESTED_BUYER = 'case__status_update_requested__buyer',
  CASE_STATUS_UPDATE_BUYER = 'case__status_update__buyer',
  CASE_STATUS_UPDATE_SELLER = 'case__status_update__seller',
  // FIXME: Added in the FE, check with BE if it is accounted for.
  CASE_MATCH_REMINDER_PROSPECT = 'case__match_reminder__prospect',
  CASE_MATCHED_BY_ANOTHER_ATTORNEY_PROSPECT = 'case__matched_by_another_attorney__prospect',
  CASE_SHARE_PC_CONTACT_INFO_PROSPECT = 'case__share_pc_contact_info__prospect',
  // Withhold Client Contact: the seller shared a represented client's contact
  CASE_CONTACT_SHARED_BUYER = 'case__contact_shared__buyer',
  CASE_CONTACT_SHARED_SELLER = 'case__contact_shared__seller',
  PUBLIC_CASE_PROPOSAL_OWNER = 'public_case__proposal__owner',
  PUBLIC_CASE_INVITE_OWNER = 'public_case__invite__owner',
  PUBLIC_CASE_INVITE_PROSPECT = 'public_case__invite__prospect',
  PRIVATE_CASE_INVITE_PROSPECT = 'private_case__invite__prospect',
  PRIVATE_CASE_PROPOSAL_OWNER = 'private_case__proposal__owner',
  PRIVATE_CASE_PROPOSAL_PROSPECT = 'private_case__proposal__prospect',
  CASE_MATCHED_OWNER = 'case__matched__owner',
  CASE_MATCHED_PROSPECT = 'case__matched__prospect',
  CASE_REJECTED_OWNER = 'case__rejected__case_owner',
  CASE_REJECTED_RELIST_CASE_OWNER = 'case_rejected_relist_case_owner',
  CASE_REJECTED_PROSPECT = 'case__rejected__prospect',
  CASE_SIGNED_OWNER = 'case__signed__case_owner',
  CASE_SIGNED_PROSPECT = 'case__signed__prospect',
  PROPOSAL_WITHDRAW_OWNER = 'proposal__withdraw__case_owner',
  PROPOSAL_WITHDRAW_PROSPECT = 'proposal__withdraw__prospect',
  CASE_COUNTEROFFER_OWNER = 'case__counteroffer__case_owner',
  CASE_COUNTEROFFER_PROSPECT = 'case__counteroffer__prospect',
  COUNTER_OFFER_RECEIVER = 'Counter_Offer_Receiver',
  COUNTER_OFFER_MAKER = 'Counter_Offer_Maker',
  PUBLIC_CASE_PROPOSAL_REJECTED_BY_OWNER = 'public_case__proposal_rejected_by_owner',
  PUBLIC_CASE_PROPOSAL_REJECTED_BY_PROSPECT = 'public_case__proposal_rejected_by_owner__prospect',
  PRIVATE_CASE_PROPOSAL_REJECTED_BY_OWNER = 'public_case__proposal_rejected_by_prospect__owner',
  PRIVATE_CASE_PROPOSAL_REJECTED_BY_PROSPECT = 'private_case__proposal_rejected_by_prospect',
  PUBLIC_CASE_MATCHED_CHARGED_BUYER = 'public_case__matched_charged__buyer',
  PUBLIC_CASE_NOTIFY_USERS = 'public_case__notify_users',
  UNREAD_CHAT_MESSAGES = 'Unread_Chat_Messages',
  LCMS_HEALTH_CHECK__FAILED = 'lcms_health_check__failed',
  LCMS_HEALTH_DAILY_REPORT_SLA_VIOLATION = 'lcms_health_daily_report__sla_violation',
  LCMS_HEALTH_DAILY_REPORT_PERFORMANCE_DEGRADED = 'lcms_health_daily_report__performance_degraded',
  LCMS_HEALTH_DAILY_REPORT_HEALTHY = 'lcms_health_daily_report__healthy',
  LCMS_HEALTH_DAILY_REPORT_RECOVERY = 'lcms_health_daily_report__recovery',
  PRIVATE_CASE_ALL_WATERFALLS_EXPIRED_OWNER = 'private_case__all_waterfalls_expired__owner',
  // Broadcast notification (MPN V2)
  PRIVATE_CASE_BROADCAST_INVITE_PROSPECT = 'private_case__broadcast_invite__prospect',
  ORGANIZATION_ATTORNEY_JOIN_REQUEST = 'organization_attorney_join_request',
  PUBLIC_CASE_DATA_ROOM_REQUESTED_CASE_OWNER = 'public_case_data_room_requested_case_owner',
  PUBLIC_CASE_DATA_ROOM_GRANTED_ACCESS_PROSPECT = 'public_case_data_room_granted_access_prospect',
  PUBLIC_CASE_DATA_ROOM_DENIED_PROSPECT = 'public_case_data_room_denied_prospect',
  PUBLIC_CASE_DATA_ROOM_REMOVED_PROSPECT = 'public_case_data_room_removed_prospect',
  CASE_REMOVED_CASE_OWNER = 'Case_Removed_Case_Owner',
  CASE_REMOVED_CASE_OPEN_NEGOTIATION = 'Case_Removed_Case_Open_Negotiation',
  CASE_CASE_MANAGER_ASSIGNED = 'case_case_manager_assigned',
  CASE_CREATED_ON_YOUR_BEHALF = 'case_created_on_your_behalf',
  CASE_PROPOSAL_CREATED_ON_YOUR_BEHALF = 'case_proposal_created_on_your_behalf',
}

// ================================
// METADATA PROPERTY TYPES
// ================================

export type CaseAvailableProspectProperties = {
  case_link: string
  case_name: string
  case_description: string
  case_id: string
  case_purchase_id: string
}

export type CaseExpiredOwnerProperties = {
  case_name: string
  case_description: string
  case_type: string
  case_link: string
  jurisdiction: string
  first_name: string
  last_name: string
  email_address: string
  client_first_name?: string
  client_last_name?: string
}

export type CaseExpiredProposalCaseOwnerProperties = {
  case_description: string
  prospect_first_name: string
  prospect_last_name: string
  prospect_firm_name: string
  case_jurisdiction: string
  case_link: string
  case_name: string
  lead_first_name: string
  lead_last_name: string
  case_type: string
  deadline: string
  fee: number
}

export type CaseExpiredProposalProspectProperties = {
  case_description: string
  owner_first_name: string
  owner_last_name: string
  owner_firm_name: string
  case_jurisdiction: string
  case_link: string
  case_name: string
  case_type: string
  deadline: string
  fee: number
}

export type CaseProposalReminderCaseOwnerProperties = {
  case_description: string
  prospect_first_name: string
  prospect_last_name: string
  prospect_firm_name: string
  case_jurisdiction: string
  case_link: string
  case_name: string
  case_type: string
  deadline: string
  fee: number
}

export type CaseWaterfallInvitationExpiredProspectProperties = {
  case_name: string
  owner_first_name: string
  owner_last_name: string
  waterfall_id: string
  case_id: string
  case_purchase_id: string
}

export type CasePrivateExpiringWaterfallProspect = {
  case_name: string
  owner_first_name: string
  owner_last_name: string
  waterfall_id: string
  case_id: string
  case_purchase_id: string
  hours_left?: number
}

export type CaseFeeAgreementReminderProperties = {
  case_link: string
  case_name: string
  case_description: string
  buyer_first_name: string
  buyer_last_name: string
  buyer_firm_name?: string
  case_jurisdiction: string
  case_type: string
  fee: number
  lead_first_name?: string
  lead_last_name?: string
}

export type CaseFeeAgreementReminderBuyerProperties = {
  case_link: string
  case_name: string
  case_description: string
  seller_first_name: string
  seller_last_name: string
  seller_firm_name?: string
  case_jurisdiction: string
  case_type: string
  fee: number
}

export type CaseFeeAgreementUploadedProperties = {
  case_link: string
  case_name: string
  case_description: string
  seller_first_name: string
  seller_last_name: string
  seller_firm_name?: string
  case_jurisdiction: string
  case_type: string
  fee: number
  lead_first_name?: string
  lead_last_name?: string
}

export type CaseStatusUpdateProperties = {
  case_id: string
  case_name: string
  lead_name: string | null
  jurisdiction: string
  jurisdiction_code: string
  case_type: string
  seller_name: string
  case_link: string
  matter_status: string
  matter_stage: string
}

export type CaseMatchedByAnotherAttorneyProspectProperties = {
  case_link: string
  case_name: string
  case_description: string
}

export type CaseSharePcContactInfoProspectProperties = {
  case_link: string
  case_name: string
  case_description: string
  lead_first_name: string
  lead_last_name: string
  lead_phone: string
  lead_email: string
}

/**
 * Withhold Client Contact share events. The title and content arrive
 * server-rendered; the metadata only carries what routing needs.
 */
export type CaseContactSharedProperties = {
  case_id: string
  case_name: string
  case_purchase_id: string
}

// Base properties common to many case purchase events
export type BaseCasePurchaseProperties = {
  case_name: string
  case_description: string
  case_link: string
  case_type: string
  jurisdiction: string
  jurisdiction_code: string
  owner_first_name: string
  owner_last_name: string
  owner_email: string
  owner_phone_number: string
  prospect_first_name: string
  prospect_last_name: string
  prospect_email: string
  prospect_phone_number: string
}

// Proposal and invite properties
export type PublicCaseProposalOwnerProperties = BaseCasePurchaseProperties & {
  buyer_first_name: string
  buyer_last_name: string
  lead_first_name: string
  lead_last_name: string
  email_address: string
  first_name: string
  last_name: string
  case_financing_source: string
  owner_firm_name: string
  prospect_firm_name: string
  case_lead_first_name: string
  case_lead_last_name: string
  case_lead_email: string
  case_lead_phone_number: string
  fee: number
  dueDate: string | null
  time_unit?: string
  timed_access_limit?: string
}

export type PublicCaseProposalProspectProperties =
  PublicCaseProposalOwnerProperties
export type PublicCaseInviteOwnerProperties = PublicCaseProposalOwnerProperties
export type PublicCaseInviteProspectProperties =
  PublicCaseProposalOwnerProperties
export type PrivateCaseInviteOwnerProperties = PublicCaseProposalOwnerProperties
export type PrivateCaseInviteProspectProperties =
  PublicCaseProposalOwnerProperties
export type PrivateCaseProposalOwnerProperties =
  PublicCaseProposalOwnerProperties
export type PrivateCaseProposalProspectProperties =
  PublicCaseProposalOwnerProperties

// Match and rejection properties
export type CaseMatchedOwnerProperties = BaseCasePurchaseProperties & {
  email_address: string
  first_name: string
  last_name: string
  case_financing_source: string
  owner_firm_name: string
  prospect_firm_name: string
  case_lead_first_name: string
  case_lead_last_name: string
  case_lead_email: string
  case_lead_phone_number: string
  fee: number
  dueDate: string | null
}

export type CaseMatchedProspectProperties = CaseMatchedOwnerProperties

export type CaseRejectedProperties = BaseCasePurchaseProperties & {
  case_date: string
  firm_name: string
  rejection_reason: string
  case_lead_first_name: string | null
  case_lead_last_name: string | null
  case_lead_email: string | null
  case_lead_phone_number: string | null
  case_referral_fee: number
}

export type CaseRejectedOwnerProperties = CaseRejectedProperties
export type CaseRejectedProspectProperties = CaseRejectedProperties
export type PublicCaseProposalRejectedByOwnerProperties = CaseRejectedProperties
export type PublicCaseProposalRejectedByProspectProperties =
  CaseRejectedProperties
export type PrivateCaseProposalRejectedByOwnerProperties =
  CaseRejectedProperties
export type PrivateCaseProposalRejectedByProspectProperties =
  CaseRejectedProperties

// Signed and withdrawal properties
export type CaseSignedOwnerProperties = BaseCasePurchaseProperties & {
  case_date: string
  firm_name: string
  case_lead_first_name: string
  case_lead_last_name: string
  case_lead_email: string
  case_lead_phone_number: string
  case_referral_fee: number
}

export type CaseSignedProspectProperties = CaseSignedOwnerProperties

export type ProposalWithdrawProperties = BaseCasePurchaseProperties & {
  case_date: string
  firm_name: string
}

export type ProposalWithdrawOwnerProperties = ProposalWithdrawProperties
export type ProposalWithdrawProspectProperties = ProposalWithdrawProperties

// Case negotiation properties (for proposals and invites)
export type CaseNegotiationProperties = {
  email_address: string
  first_name: string
  last_name: string
  case_link: string
  case_name: string
  case_description: string
  case_type: string
  case_financing_source: string
  owner_first_name: string
  owner_last_name: string
  owner_firm_name: string
  prospect_first_name: string
  prospect_last_name: string
  prospect_firm_name: string
  jurisdiction: string
  jurisdiction_code: string
  case_lead_first_name: string
  case_lead_last_name: string
  case_lead_email: string
  case_lead_phone_number: string
  fee: number
  dueDate: string | null
  time_unit: string
  timed_access_limit: string
}

// Counteroffer properties
export type CaseCounterofferProperties = {
  email_address: string
  first_name: string
  last_name: string
  case_name: string
  case_description: string
  case_link: string
  maker_first_name: string
  maker_last_name: string
  case_date: string
  case_type: string
  jurisdiction: string
  jurisdiction_code: string
  fee: number
}

export type CaseCounterofferOwnerProperties = CaseCounterofferProperties
export type CaseCounterofferProspectProperties = CaseCounterofferProperties
export type CounterOfferReceiverProperties = CaseCounterofferProperties
export type CounterOfferMakerProperties = CaseCounterofferProperties

export type PublicCaseMatchedChargedBuyerProperties = {
  case_description: string
  case_id: string
  case_jurisdiction: string
  case_jurisdiction_code: string
  case_link: string
  case_name: string
  case_type: string
  firm_name: string
  owner_first_name: string
  owner_last_name: string
  owner_email: string
  owner_phone_number: string
  case_lead_first_name: string
  case_lead_last_name: string
  case_lead_email: string
  case_lead_phone_number: string
  case_referral_fee: number
  prospect_first_name: string
  prospect_last_name: string
  prospect_email: string
  prospect_phone_number: string
  amount: number
}

export type PublicCaseNotifyUsersProperties = {
  case_description: string
  case_id: string
  case_jurisdiction: string
  case_jurisdiction_code: string
  case_link: string
  case_name: string
  case_type: string
  fee: number
  notifications_enabled_sms: boolean
  notifications_enabled_email: boolean
  community_exclusive_expires_at: string
  communities: []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export type UnreadChatMessagesProperties = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export type LCMSHealthCheckProperties = {
  lcms_config_id: string
  lcms_provider: string
  organization_id: string
  error_message: string
  http_status_code: number
  health_check_timestamp: string
  org_admin_name: string
}

export type LCMSHealthDailyReportSlaViolationProperties = {
  lcms_config_id: string
  provider: string
  organization_id: string
  total_checks: number
  failed_checks: number
  days_since_last_failure: number
  reliability_score: number
  errors_by_status_code: Record<string, number>
  first_failure_time?: string
  report_date: string
}

export type LCMSHealthDailyReportPerformanceDegradedProperties = {
  lcms_config_id: string
  provider: string
  organization_id: string
  estimated_response_time: number
  total_checks: number
  reliability_score: number
  report_date: string
}

export type LCMSHealthDailyReportHealthyProperties = {
  lcms_config_id: string
  provider: string
  organization_id: string
  total_checks: number
  estimated_response_time: number
  reliability_score: number
  days_since_last_failure: number
  report_date: string
}

export type LCMSHealthDailyReportRecoveryProperties = {
  lcms_config_id: string
  provider: string
  organization_id: string
  last_success_time?: string
  total_checks: number
  successful_checks: number
  reliability_score: number
  days_since_last_failure: number
  report_date: string
}

export type PrivateCaseAllWaterfallsExpiredOwnerProperties = {
  client_name: string
}

export type PrivateCaseBroadcastInviteProspectProperties = {
  case_id: string
  case_name: string
  case_link: string
  case_description: string
  case_type: string
  jurisdiction: string
  jurisdiction_code: string
  owner_first_name: string
  owner_last_name: string
  owner_firm_name: string
  fee: number
  prospect_first_name: string
  prospect_last_name: string
  prospect_firm_name: string
}

export type OrganizationAttorneyJoinRequestProperties = {
  attorney_last_name: string
  attorney_first_name: string
  attorney_email: string
  org_creator_name: string
  org_creator_email: string
  firm_settings_link: string
}

export type PublicCaseDataRoomRequestedCaseOwnerProperties = {
  buyer_attorney_first_name: string
  buyer_attorney_last_name: string
  client_first_name?: string
  client_last_name?: string
  case_name: string
}

export type PublicCaseDataRoomGrantedAccessProspectProperties = {
  client_first_name?: string
  client_last_name?: string
  case_name: string
}

export type PublicCaseDataRoomDeniedProspectProperties = {
  client_first_name?: string
  client_last_name?: string
  case_name: string
}

export type PublicCaseDataRoomRemovedProspectProperties = {
  client_first_name?: string
  client_last_name?: string
  case_name: string
}

export type CaseRemovedCaseOwnerProperties = {
  case_name: string
  client_first_name?: string
  client_last_name?: string
}

export type CaseRemovedCaseOpenNegotiationProperties = {
  case_name: string
}

export type CaseCaseManagerAssignedProperties = {
  client_first_name?: string
  client_last_name?: string
  case_name: string
}

export type CaseCreatedOnYourBehalfProperties = {
  proxy_user_first_name: string
  proxy_user_last_name: string
  client_first_name?: string
  client_last_name?: string
  case_name: string
  case_link: string
}

export type CaseProposalCreatedOnYourBehalfProperties = {
  proxy_user_first_name: string
  proxy_user_last_name: string
  case_name: string
  case_link: string
}

// ================================
// EVENT PROPERTIES MAP
// ================================

/**
 * Maps each EventPayloadId to its corresponding metadata property type
 * Enables type-safe metadata access based on notification type
 */
export type EventPropertiesMap = {
  [EventPayloadId.CASE_AVAILABLE_PROSPECT]: CaseAvailableProspectProperties
  [EventPayloadId.CASE_EXPIRED_OWNER]: CaseExpiredOwnerProperties
  [EventPayloadId.CASE_EXPIRED_PROPOSAL_CASE_OWNER]: CaseExpiredProposalCaseOwnerProperties
  [EventPayloadId.CASE_EXPIRED_PROPOSAL_PROSPECT]: CaseExpiredProposalProspectProperties
  [EventPayloadId.CASE_PROPOSAL_REMINDER_CASE_OWNER]: CaseProposalReminderCaseOwnerProperties
  [EventPayloadId.CASE_WATERFALL_INVITATION_EXPIRED_PROSPECT]: CaseWaterfallInvitationExpiredProspectProperties
  [EventPayloadId.CASE_PRIVATE_EXPIRING_WATERFALL_PROSPECT]: CasePrivateExpiringWaterfallProspect
  [EventPayloadId.CASE_FEE_AGREEMENT_REMINDER]: CaseFeeAgreementReminderProperties
  [EventPayloadId.CASE_FEE_AGREEMENT_REMINDER_BUYER]: CaseFeeAgreementReminderBuyerProperties
  [EventPayloadId.CASE_FEE_AGREEMENT_UPLOADED]: CaseFeeAgreementUploadedProperties
  [EventPayloadId.CASE_STATUS_UPDATE_REMINDER_BUYER]: CaseStatusUpdateProperties
  [EventPayloadId.CASE_STATUS_UPDATE_REQUESTED_BUYER]: CaseStatusUpdateProperties
  [EventPayloadId.CASE_STATUS_UPDATE_BUYER]: CaseStatusUpdateProperties
  [EventPayloadId.CASE_STATUS_UPDATE_SELLER]: CaseStatusUpdateProperties
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [EventPayloadId.CASE_MATCH_REMINDER_PROSPECT]: any
  [EventPayloadId.CASE_MATCHED_BY_ANOTHER_ATTORNEY_PROSPECT]: CaseMatchedByAnotherAttorneyProspectProperties
  [EventPayloadId.CASE_SHARE_PC_CONTACT_INFO_PROSPECT]: CaseSharePcContactInfoProspectProperties
  [EventPayloadId.CASE_CONTACT_SHARED_BUYER]: CaseContactSharedProperties
  [EventPayloadId.CASE_CONTACT_SHARED_SELLER]: CaseContactSharedProperties
  [EventPayloadId.PUBLIC_CASE_PROPOSAL_OWNER]: CaseNegotiationProperties
  [EventPayloadId.PUBLIC_CASE_INVITE_OWNER]: CaseNegotiationProperties
  [EventPayloadId.PUBLIC_CASE_INVITE_PROSPECT]: CaseNegotiationProperties
  [EventPayloadId.PRIVATE_CASE_INVITE_PROSPECT]: CaseNegotiationProperties
  [EventPayloadId.PRIVATE_CASE_PROPOSAL_OWNER]: CaseNegotiationProperties
  [EventPayloadId.PRIVATE_CASE_PROPOSAL_PROSPECT]: CaseNegotiationProperties
  [EventPayloadId.CASE_MATCHED_OWNER]: CaseMatchedOwnerProperties
  [EventPayloadId.CASE_MATCHED_PROSPECT]: CaseMatchedProspectProperties
  [EventPayloadId.CASE_REJECTED_OWNER]: CaseRejectedOwnerProperties
  [EventPayloadId.CASE_REJECTED_RELIST_CASE_OWNER]: CaseRejectedOwnerProperties
  [EventPayloadId.CASE_REJECTED_PROSPECT]: CaseRejectedProspectProperties
  [EventPayloadId.CASE_SIGNED_OWNER]: CaseSignedOwnerProperties
  [EventPayloadId.CASE_SIGNED_PROSPECT]: CaseSignedProspectProperties
  [EventPayloadId.PROPOSAL_WITHDRAW_OWNER]: ProposalWithdrawOwnerProperties
  [EventPayloadId.PROPOSAL_WITHDRAW_PROSPECT]: ProposalWithdrawProspectProperties
  [EventPayloadId.CASE_COUNTEROFFER_OWNER]: CaseCounterofferOwnerProperties
  [EventPayloadId.CASE_COUNTEROFFER_PROSPECT]: CaseCounterofferProspectProperties
  [EventPayloadId.COUNTER_OFFER_RECEIVER]: CounterOfferReceiverProperties
  [EventPayloadId.COUNTER_OFFER_MAKER]: CounterOfferMakerProperties
  [EventPayloadId.PUBLIC_CASE_PROPOSAL_REJECTED_BY_OWNER]: PublicCaseProposalRejectedByOwnerProperties
  [EventPayloadId.PUBLIC_CASE_PROPOSAL_REJECTED_BY_PROSPECT]: PublicCaseProposalRejectedByProspectProperties
  [EventPayloadId.PRIVATE_CASE_PROPOSAL_REJECTED_BY_OWNER]: PrivateCaseProposalRejectedByOwnerProperties
  [EventPayloadId.PRIVATE_CASE_PROPOSAL_REJECTED_BY_PROSPECT]: PrivateCaseProposalRejectedByProspectProperties
  [EventPayloadId.PUBLIC_CASE_MATCHED_CHARGED_BUYER]: PublicCaseMatchedChargedBuyerProperties
  [EventPayloadId.PUBLIC_CASE_NOTIFY_USERS]: PublicCaseNotifyUsersProperties
  [EventPayloadId.UNREAD_CHAT_MESSAGES]: UnreadChatMessagesProperties
  [EventPayloadId.LCMS_HEALTH_CHECK__FAILED]: LCMSHealthCheckProperties
  [EventPayloadId.LCMS_HEALTH_DAILY_REPORT_SLA_VIOLATION]: LCMSHealthDailyReportSlaViolationProperties
  [EventPayloadId.LCMS_HEALTH_DAILY_REPORT_PERFORMANCE_DEGRADED]: LCMSHealthDailyReportPerformanceDegradedProperties
  [EventPayloadId.LCMS_HEALTH_DAILY_REPORT_HEALTHY]: LCMSHealthDailyReportHealthyProperties
  [EventPayloadId.LCMS_HEALTH_DAILY_REPORT_RECOVERY]: LCMSHealthDailyReportRecoveryProperties
  [EventPayloadId.PRIVATE_CASE_ALL_WATERFALLS_EXPIRED_OWNER]: PrivateCaseAllWaterfallsExpiredOwnerProperties
  [EventPayloadId.PRIVATE_CASE_BROADCAST_INVITE_PROSPECT]: PrivateCaseBroadcastInviteProspectProperties
  [EventPayloadId.ORGANIZATION_ATTORNEY_JOIN_REQUEST]: OrganizationAttorneyJoinRequestProperties
  [EventPayloadId.PUBLIC_CASE_DATA_ROOM_REQUESTED_CASE_OWNER]: PublicCaseDataRoomRequestedCaseOwnerProperties
  [EventPayloadId.PUBLIC_CASE_DATA_ROOM_GRANTED_ACCESS_PROSPECT]: PublicCaseDataRoomGrantedAccessProspectProperties
  [EventPayloadId.PUBLIC_CASE_DATA_ROOM_DENIED_PROSPECT]: PublicCaseDataRoomDeniedProspectProperties
  [EventPayloadId.PUBLIC_CASE_DATA_ROOM_REMOVED_PROSPECT]: PublicCaseDataRoomRemovedProspectProperties
  [EventPayloadId.CASE_REMOVED_CASE_OWNER]: CaseRemovedCaseOwnerProperties
  [EventPayloadId.CASE_REMOVED_CASE_OPEN_NEGOTIATION]: CaseRemovedCaseOpenNegotiationProperties
  [EventPayloadId.CASE_CASE_MANAGER_ASSIGNED]: CaseCaseManagerAssignedProperties
  [EventPayloadId.CASE_CREATED_ON_YOUR_BEHALF]: CaseCreatedOnYourBehalfProperties
  [EventPayloadId.CASE_PROPOSAL_CREATED_ON_YOUR_BEHALF]: CaseProposalCreatedOnYourBehalfProperties
}

/**
 * Helper type for type-safe metadata access
 * Usage: NotificationMetadata<EventPayloadId.CASE_MATCHED_OWNER>
 */
export type NotificationMetadata<T extends EventPayloadId> =
  EventPropertiesMap[T]
